// apps/client/src/ui/screens/donate.tsx (M2-T10) — giving the museum what it is missing: a slot grid of the fossils and fish in your
// pockets (and hand) the museum does not have yet. Tap one to donate it; Professor Reed thanks you by name, and marks a completed hall.
// A refusal he would voice (already have it, not a fossil or fish, not identified) is his line (G13); any other shows the reject toast.
import type { ItemId, ItemView } from '@whiskerton/shared';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getGame } from '../../game/instance';
import type { CuratorRefusal } from '../../state/slices/museum';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Bubble, Button, Panel } from '../kit';
import type { ScreenDef } from '../router';
import { injectMuseumStyle, inlineName, nameOf } from './museum';

declare module '../router' { interface ScreenParams { donate: undefined } }

const REFUSAL: Readonly<Record<CuratorRefusal, 'curator.alreadyDonated' | 'curator.notDonatable' | 'curator.unidentified'>> = {
  already_donated: 'curator.alreadyDonated', not_donatable: 'curator.notDonatable', unidentified: 'curator.unidentified',
};

function Cell({ item, busy, onPick }: { item: ItemView; busy: boolean; onPick(): void }) {
  const icon = useIcon(item.catalogId);
  return (
    <button type="button" className="slot" aria-label={nameOf(item.catalogId)} disabled={busy} onClick={onPick}>
      <img src={icon} alt="" />
    </button>
  );
}

function Donate({ close }: { close: () => void }) {
  injectMuseumStyle();
  const t = useT();
  const { space, donate, player } = useGame(useShallow((s) => ({ space: s.space, donate: s.museumDonate,
    player: s.you ? (s.residents[s.you.resident]?.name ?? '') : '' })));
  const donatable = useGame(useShallow((s) => s.museumDonatable()));       // pockets/hand minus what the museum has; same items → no re-render
  const [thanks, setThanks] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (space !== 'museum') close(); }, [space, close]);   // left the museum under the panel
  const give = (id: ItemId, name: string) => {
    setBusy(true);
    void donate(id).then((r) => {
      if (!r) return;
      if ('rejected' in r) { setThanks(t(REFUSAL[r.rejected], { item: name })); return; }
      getGame().sound?.sfx('coin');
      const done = r.hallComplete ? ` ${t(r.hallComplete === 'fossils' ? 'curator.hallFossils' : 'curator.hallFish')}` : '';
      setThanks(`${t('curator.donateThanks', { player, item: name })}${done}`);
    }).finally(() => setBusy(false));
  };
  return (
    <Panel title={t('museum.donateTitle')} className="shop-panel museum-panel">
      <div className="donate-head">
        {thanks || donatable.length > 0 ? <Bubble name={t('curator.name')}>{thanks ?? t('curator.donatePrompt')}</Bubble>
          : <p className="shop-note">{t('museum.donateEmpty')}</p>}
        <Button variant="quiet" onClick={close}>{t('museum.bye')}</Button>
      </div>
      {donatable.length > 0 && (
        <div className="slot-grid donate-slots">
          {donatable.map((i) => <Cell key={i.id} item={i} busy={busy} onPick={() => give(i.id, inlineName(i.catalogId))} />)}
        </div>
      )}
    </Panel>
  );
}
export const screen: ScreenDef<'donate'> = { id: 'donate', layer: 'panel', component: ({ close }) => <Donate close={close} /> };
